import { Position } from '../types/Position'
import { GeolocationOptions } from '../types/GeolocationOptions'

export class GeolocationService {
    static async getCurrentPosition(
        options: GeolocationOptions = {}
    ): Promise<Position> {
        const {
            enableHighAccuracy = true,
            timeout = 10000,
            maximumAge = 300000 // 5 min
        } = options

        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject({
                    code: 0,
                    message: 'Geolocation is not supported by this browser'
                })

                return
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        altitude: position.coords.altitude,
                        altitudeAccuracy: position.coords.altitudeAccuracy,
                        heading: position.coords.heading,
                        speed: position.coords.speed,
                        timestamp: position.timestamp
                    })
                },
                (error) => {
                    const errorMap: { [key: number]: string } = {
                        1: 'Access to geolocation is denied',
                        2: 'Location information is unavailable',
                        3: 'The request timed out'
                    }

                    reject({
                        code: error.code,
                        message: errorMap[error.code] || 'Unknown error'
                    })
                },
                { enableHighAccuracy, timeout, maximumAge }
            )
        })
    }

    static async watchPosition(
        callback: (position: Position) => void,
        options: GeolocationOptions = {}
    ): Promise<number> {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation is not supported'))

                return
            }

            const watchId = navigator.geolocation.watchPosition(
                (position) => {
                    callback({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        altitude: position.coords.altitude,
                        altitudeAccuracy: position.coords.altitudeAccuracy,
                        heading: position.coords.heading,
                        speed: position.coords.speed,
                        timestamp: position.timestamp
                    })
                },
                (error) => {
                    console.error('Position tracking error:', error)
                },
                options
            )

            resolve(watchId)
        })
    }

    static clearWatch(watchId: number): void {
        navigator.geolocation.clearWatch(watchId)
    }
}